import { useState, useEffect } from 'react';
import { QuickDateRange } from '@/components/ui/date-range-quick-filter';

interface DateRange {
  from: string;
  to: string;
}

interface UseDateRangeDefaultsReturn {
  quickRange: QuickDateRange;
  dateFrom: string;
  dateTo: string;
  setQuickRange: (range: QuickDateRange, dates: DateRange) => void;
  setManualDates: (from: string, to: string) => void;
}

const getDefaultRangeForReport = (reportType: string): QuickDateRange => {
  const defaults: Record<string, QuickDateRange> = {
    sales: 'today',      // Reporte de ventas: día actual
    kardex: 'current-month',    // Kárdex: mes en curso
    stock: 'today',             // Stock: día actual
    topProducts: 'last-week',   // Top productos: última semana
    consumption: 'current-month' // Consumo: mes en curso
  };

  return defaults[reportType] || 'today';
};

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateRange = (range: QuickDateRange): DateRange => {
  const today = new Date();
  const todayStr = formatLocalDate(today);

  switch (range) {
    case 'today':
      return { from: todayStr, to: todayStr };

    case 'yesterday':
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return { from: formatLocalDate(yesterday), to: formatLocalDate(yesterday) };

    case 'last-week':
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 6);
      return {
        from: formatLocalDate(lastWeek),
        to: todayStr
      };

    case 'current-month':
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        from: formatLocalDate(firstDayOfMonth),
        to: todayStr
      };

    case 'custom':
    default:
      return { from: '', to: '' };
  }
};

export const useDateRangeDefaults = (reportType: string): UseDateRangeDefaultsReturn => {
  const [quickRange, setQuickRangeState] = useState<QuickDateRange>('custom');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  useEffect(() => {
    // Cargar preferencia guardada o usar valor por defecto
    const savedRange = localStorage.getItem(`dateRange_${reportType}`) as QuickDateRange;
    const defaultRange = savedRange || getDefaultRangeForReport(reportType);

    if (defaultRange !== 'custom') {
      const dates = getDateRange(defaultRange);
      setQuickRangeState(defaultRange);
      setDateFrom(dates.from);
      setDateTo(dates.to);
    } else {
      setQuickRangeState('custom');
      // Para custom, usar fechas vacías o del día actual
      const todayStr = formatLocalDate(new Date());
      setDateFrom(todayStr);
      setDateTo(todayStr);
    }
  }, [reportType]);

  const setQuickRange = (range: QuickDateRange, dates: DateRange) => {
    setQuickRangeState(range);
    setDateFrom(dates.from);
    setDateTo(dates.to);

    // Guardar preferencia
    if (range !== 'custom') {
      localStorage.setItem(`dateRange_${reportType}`, range);
    }
  };

  const setManualDates = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);

    // Si se cambian las fechas manualmente, cambiar a "custom"
    if (quickRange !== 'custom') {
      setQuickRangeState('custom');
    }
  };

  return {
    quickRange,
    dateFrom,
    dateTo,
    setQuickRange,
    setManualDates
  };
};